/**
 * Toast notification context
 * Provides centralized toast state management across the entire app
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'info' | 'warning' | 'error';
  duration?: number;
  action?: ToastAction;
}

export interface ShowToastOptions {
  type?: Toast['type'];
  duration?: number;
  action?: ToastAction;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (message: string, typeOrOptions?: Toast['type'] | ShowToastOptions, duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((
    message: string,
    typeOrOptions: Toast['type'] | ShowToastOptions = 'info',
    legacyDuration?: number
  ) => {
    // Support both old signature (type, duration) and new signature (options object)
    let type: Toast['type'] = 'info';
    let duration: number = 4000;
    let action: ToastAction | undefined;

    if (typeof typeOrOptions === 'string') {
      // Old signature: showToast(message, type, duration)
      type = typeOrOptions;
      duration = legacyDuration ?? 4000;
    } else if (typeOrOptions) {
      // New signature: showToast(message, options)
      type = typeOrOptions.type ?? 'info';
      duration = typeOrOptions.duration ?? 4000;
      action = typeOrOptions.action;
    }

    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast: Toast = { id, message, type, duration, action };

    // Replace any existing toasts - only show one at a time
    setToasts([newToast]);

    // Auto-remove after duration (longer duration if there's an action)
    const actualDuration = action ? Math.max(duration, 6000) : duration;
    if (actualDuration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, actualDuration);
    }
  }, [removeToast]);

  const value: ToastContextValue = {
    toasts,
    showToast,
    removeToast
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToastContext = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};
